import PageHero from "@/components/base/PageHero";
import FeedbackForm from "./components/FeedbackForm";
import { siteInfo } from "@/mocks/siteInfo";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-file-list-3-line",
    title: "结构清晰",
    desc: "以剧集、角色、资讯三大模块组织内容，逐集与逐角色独立成页，方便按需查阅。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息详实",
    desc: "每一条内容都包含背景、关系与剧情节点的完整梳理，拒绝只言片语的碎片信息。",
  },
  {
    icon: "ri-heart-3-line",
    title: "面向观众",
    desc: "站在追剧者的角度组织信息，帮助观众理解剧情进展与人物动机背后的逻辑。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于本站 - 年轻的母亲2影视内容站",
    description:
      "了解《年轻的母亲2》影视内容站的定位与内容范围。本站提供剧情介绍、角色分析与影视资讯，并说明联系方式、版权与免责声明。",
    keywords: "年轻的母亲2,关于本站,剧情介绍,角色分析,影视资讯",
  });

  return (
    <div className="w-full">
      <PageHero
        eyebrow="关于本站"
        title="关于《年轻的母亲2》影视内容站"
        desc="我们是一群家庭剧爱好者，希望用专业、结构化的方式，把一部剧的剧情与人物讲清楚。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "关于本站" }]}
      />

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-12">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>
              网站定位说明
            </h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              {siteInfo.intro}
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              本站定位为专业电视剧内容聚合平台，专注于《年轻的母亲2》及其相关剧情、角色与资讯的深度展现。我们面向年轻及中年家庭剧爱好者、电视剧粉丝以及影视内容研究者，主要提供全面的剧情与角色资料，同时配以便捷的剧集浏览和资讯获取体验。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                版权与免责声明
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                  本站为内容演示站点，所展示的剧集信息、角色资料、演员姓名与剧照均为虚构示例，仅用于演示目的。
                </li>
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                  本站不提供任何影视播放、下载或购买服务，相关内容介绍仅用于剧情整理与交流。
                </li>
                <li className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                  收藏功能仅将状态保存在你的浏览器本地，不会上传至任何服务器，刷新页面后仍可恢复。
                </li>
              </ul>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-mail-send-line text-lg"></i>
                </span>
                联系方式
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                有任何建议、内容纠错或合作意向，欢迎通过下方邮箱或反馈表单联系我们。
              </p>
              <p className="mt-3 flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-mail-line text-base"></i>
                </span>
                {siteInfo.contactEmail}
              </p>

              <div className="mt-5 border-t border-background-200 pt-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950">内容反馈</h3>
                <div className="mt-4">
                  <FeedbackForm />
                </div>
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
}